import React, { useState, useEffect, useContext, Fragment } from "react";

import {
  Form,
  Heading,
  Element,
  Content,
  Message,
} from "react-bulma-components";
import dayjs from "dayjs";
import minMax from "dayjs/plugin/minMax";

import ErrorMessage from "util/errorMessage";
import StructuredText from "common-frontend/util/structuredText";

import { Dollars } from "util/currency";
import { FORM_STATE } from "./registrationForms";
import { PromptQuestions, adjustQuestionResponses } from "./questions";

import TrimmedInput from "common-frontend/util/trimmedInput";
import RefundContactNotice from "./refundContactNotice";

import { ucFirst, ucFirstEachWord, a_or_an } from "util/text";

import capacityInformation from "./capacity";
import { participationStateContext } from "./load";

import {
  adjustFeeQualifyingDetails,
  AskFeeQualifyingData,
} from "./feeQualification";

const { Field, Label, Control, Radio, Select, Checkbox } = Form;
dayjs.extend(minMax);

export const IN_PERSON = "in person";
export const REMOTELY = "remotely";

export const isFalse = (v) => !(v ?? true);
export const isRemotelyOrInPerson = (v) => v === IN_PERSON || v === REMOTELY;
export const isSimpleTrue = (v) => v && !isRemotelyOrInPerson(v);

// WHAT HAPPENS IF SOME INSTANCES OF THE ITEM HAVE ALREADY BEEN PURCHASED?
// =======================================================================

// One of two gatekeeper components is displayed in this case:

//   * If the registrant has already purchased the maximum number of
//     instances permitted by the item's purchase_limit setting, the
//     <AlreadyPurchasedMaximumAllowable> component is rendered. It
//     shows the number purchased and gives the option of cancelling
//     some or all of them.

//   * Otherwise, the <AddMoreOrCancel> component is rendered. As well as
//     showing number of instances purchased and giving the option of
//     cancelling some or all of them, it gives the option of adding more
//     (or going on a waiting list if no more are available).

// Cancellation is implemented by directly setting quantity to the appropriate
// negative number (and clearing any previously set waiting list request).

// Adding more cannot be implemented by directly setting a positive quantity,
// because there might be a waiting list; instead, an addMore state variable
// is defined by the main component and is set by the AddMoreOrCancel component
// when the option to add more is selected.

// Then, the components to allow the person to purchase additional instances
// of the item, or to go on a waiting list if none are available, are rendered
// only if addMore is set (or if quantity is somehow already positive).

// When the registrant does NOT have any previous instances purchased, the
// components allowing for purchase are always rendered, and the addMore
// value is ignored.

// Here are these two gatekeeper components. They both use a common pair
// of subcomponents, <CancellationOption> (inside a Field) for the
// actual checkbox where the registrant expresses the desire to cancel, and
// <CancellationQuantity> (standing alone) that renders null except when
// a cancellation desire has been expressed (quantity is negative) and
// more than one instance has previously been purchased, in which case it
// lets the registrant choose how many to cancel.

function AlreadyPurchasedMaximumAllowable(props) {
  const { item, numberAlreadyPurchased } = props;
  const { item_code, purchase_limit } = item;

  return (
    <Content>
      You already have{" "}
      {purchase_limit === 1
        ? a_or_an(item_code)
        : `${numberAlreadyPurchased} ${item_code}, which is the maximum ` +
          "available per person"}
      .
      <Field>
        <CancellationOption {...props} />
      </Field>
      <CancellationQuantity {...props} />
    </Content>
  );
}

function AddMoreOrCancel(props) {
  const {
    numberAlreadyPurchased,
    amountPreviouslyPaid,
    currentDetails,
    adjustDetails,
    addMore,
    setAddMore,
  } = props;

  const { quantity } = currentDetails;

  return (
    <Content>
      <p>
        You currently have {numberAlreadyPurchased}.
        {amountPreviouslyPaid
          ? ` Amount paid: ${Dollars.format(amountPreviouslyPaid)}.`
          : null}
      </p>
      <Field>
        <Control>
          <Checkbox
            checked={addMore || quantity > 0}
            onChange={(e) => {
              setAddMore(e.target.checked);
              if (quantity > 0 && !e.target.checked) {
                adjustDetails({ quantity: 0 });
              }
            }}
          >
            {amountPreviouslyPaid ? " Purchase more" : " Add more"}
          </Checkbox>
        </Control>
        <CancellationOption {...props} />
      </Field>
      <CancellationQuantity {...props} />
    </Content>
  );
}

function CancellationOption({
  item,
  numberAlreadyPurchased,
  amountPreviouslyPaid,
  currentDetails,
  adjustDetails,
  setAddMore,
}) {
  const setCancel = () => {
    if (setAddMore) setAddMore(false);
    if (currentDetails.quantity < 0) return;
    else adjustDetails({ quantity: -1 });
  };
  const clearCancel = () => {
    if (currentDetails.quantity < 0) adjustDetails({ quantity: 0 });
  };

  return (
    <Fragment>
      <Control>
        <Checkbox
          checked={currentDetails.quantity < 0}
          onChange={(e) => (e.target.checked ? setCancel() : clearCancel())}
        >
          Cancel{" "}
          {numberAlreadyPurchased === 1
            ? item.purchase_limit === 1
              ? "it"
              : "the one you have"
            : "one or more of them"}
          {amountPreviouslyPaid ? "*" : ""}
        </Checkbox>
      </Control>
      {amountPreviouslyPaid ? (
        <p>
          *Please note that cancelling does not automatically trigger a refund
          of the {Dollars.format(amountPreviouslyPaid)} previously paid.
          <RefundContactNotice ofWhat="part of this amount" isNew />
        </p>
      ) : null}
    </Fragment>
  );
}

function CancellationQuantity({
  item,
  numberAlreadyPurchased,
  amountPreviouslyPaid,
  currentDetails,
  adjustDetails,
}) {
  // In theory it is possible for number of items to be unlimited, but
  // in practice we never will have that situation, and if we do it is
  // unlikely a large number will need to be cancelled, so don't bother with
  // a separate numeric input field the way we do in HowManyPurchase below;
  // just use a select widget, and if there are more than 50, require the
  // person to cancel 50 at a time.
  const maxCancellable = Math.min(50, numberAlreadyPurchased);
  if (numberAlreadyPurchased > 1 && currentDetails.quantity < 0)
    return (
      <Field>
        <Label>Cancel How Many?</Label>
        <Control>
          <Select
            onChange={(e) =>
              adjustDetails({ quantity: -Number(e.target.value) })
            }
          >
            {Array.from(Array(maxCancellable).keys()).map((i) => (
              <option
                key={i + 1}
                value={i + 1}
                selected={currentDetails.quantity === -(i + 1)}
              >
                {i + 1}
              </option>
            ))}
          </Select>
        </Control>
      </Field>
    );
  else return null;
}

// ONCE PAST THE GATEKEEER: COMPONENTS FOR ACTUALLY PURCHASING NOT CANCELLING

// The following component is rendered when allowing the participant to
// purchase a limit-of-one-per-person type of item, where the item_code is
// a singular noun and the prompt is a yes/no question of the form
// "Do you require a(n) .... ?" or "Do you want to purchase a(n) ... ?"

// The component is also used for all types of items (even ones where more
// than one per person is normally allowed) when the item is sold out and
// there is simply a yes/no question about being added to (or remaining on)
// a waiting list in case additional items become available.

function YesNoPurchase({
  item,
  numberAlreadyPurchased,
  creditBalance,
  currentDetails,
  adjustDetails,
  amount,
  formattedPriceString,
  available,
  waitingList,
}) {
  const { item_code } = item;
  const anItem = numberAlreadyPurchased
    ? `an additional {item_code}`
    : a_or_an(item_code);
  const require = amount === null ? "require" : "wish to purchase";
  const itemKey = `purchase-${item.activity_id}-${item_code}`;

  const onWaitingList = waitingList.position !== null;
  const creditAvailable =
    creditBalance > 0 ? Math.min(amount, creditBalance) : 0;
  const amountOverride = creditAvailable > 0 ? amount - creditAvailable : null;

  const optionNo = (
    <Control>
      <Radio
        name={itemKey}
        checked={
          currentDetails.quantity === 0 && !currentDetails.waitlist_desired
        }
        onChange={() =>
          adjustDetails({
            quantity: 0,
            total_amount_override: null,
            waitlist_desired: null,
          })
        }
      >
        {" "}
        No
        {onWaitingList
          ? available.toMe > 0
            ? ", offer it to the next person, and "
            : ", "
          : null}
        {onWaitingList ? "remove me from the waiting list" : null}
      </Radio>
    </Control>
  );
  const optionYes = (
    <Control>
      <Radio
        name={itemKey}
        checked={currentDetails.quantity === 1}
        onChange={() =>
          adjustDetails({
            quantity: 1,
            total_amount_override: amountOverride,
            waitlist_desired: null,
          })
        }
      >
        {" "}
        Yes.
        {amount
          ? creditAvailable > 0
            ? ` Cost is ${Dollars.format(amountOverride)} ` +
              `(${formattedPriceString}, less ${Dollars.format(
                creditAvailable
              )} previously paid)`
            : ` Cost is ${formattedPriceString}`
          : null}
      </Radio>
    </Control>
  );

  const optionWait = (
    <Control>
      <Radio
        name={itemKey}
        checked={
          currentDetails.quantity === 0 && currentDetails.waitlist_desired
        }
        onChange={() => adjustDetails({ quantity: 0, waitlist_desired: true })}
      >
        Yes,
        {onWaitingList ? " keep me on " : " add me to "}the waiting list
      </Radio>
    </Control>
  );

  // Participant was on a waiting list, and item is now available. (This means
  // is it is a max-one-per-person item; this component is not called for
  // other types of items unless there are none available).
  const nowAvailableMessage = (
    <Message color="success">
      <Message.Body>
        You have been on a waiting list for {anItem}. One is now available. Do
        you still {require} it?
      </Message.Body>{" "}
    </Message>
  );

  // Item is not available, and participant is not yet on a waiting list.
  const unavailableMessage = (
    <Message color="warning">
      <Message.Body>
        These are currently sold out. Do you wish to place your name on a
        waiting list in case of cancellations? (There{" "}
        {waitingList.length === 0
          ? "are no people "
          : waitingList.length === 1
          ? "is one person "
          : `are ${waitingList.length} people `}
        currently on the waiting list).
      </Message.Body>{" "}
    </Message>
  );

  // Item is not available, and participant is already on a waiting list.
  const stillUnavailableMessage = (
    <Message color="warning">
      <Message.Body>
        These are currently sold out, but your name is on a waiting list in case
        of cancellations. You are currently at position #
        {waitingList.position + 1} on the waiting list. Do you wish to remain on
        the waiting list?
      </Message.Body>{" "}
    </Message>
  );
  return (
    <Field>
      {onWaitingList ? (
        available.toMe === 0 ? (
          stillUnavailableMessage
        ) : (
          nowAvailableMessage
        )
      ) : available.toMe === 0 ? (
        unavailableMessage
      ) : (
        <Label>
          Do you {require} {anItem}?
        </Label>
      )}

      {onWaitingList ? (
        available.toMe === 0 ? (
          <Fragment>
            {optionWait} {optionNo}
          </Fragment>
        ) : (
          <Fragment>
            {optionYes} {optionNo}
          </Fragment>
        )
      ) : available.toMe === 0 ? (
        <Fragment>
          {optionNo} {optionWait}
        </Fragment>
      ) : (
        <Field kind="group">
          {optionNo} {optionYes}
        </Field>
      )}
    </Field>
  );
}

// The following component is rendered when allowing the participant to
// purchase an item that permits more than one per person, item_code is
// a plural noun, and the prompt is a question of the form "How many
// ... do you require?" or "How many ... do you want to purchase?"

// Even though the item generally permits more than one per person, the
// person may only be permitted to purchase one at this particular time,
// if they already have one less than the maximum allowed, or if there
// is only one remaining generally with the rest being sold out. Note, though,
// this component will NOT be called if there are none available; either
// AlreadyPurchasedMaximumAllowable or YesNoPurchase will be called in those
// cases (the former if the person has already acquired the maximum permitted,
// the latter if all are sold out and they are asked yes/no to going on a
// waiting list).

// If the number available for purchase is <= MAX_SELECT_LENGTH, a select
// element will be used -- and, in this case, an "almost sold out" warning
// together with a "go on the waiting list for more" option are added if
// the number available for purchase is less than the per-person limit

// Otherwise, a free-form numeric input element will be used, and there will
// be no "almost sold out" warning (even if the number available is technically
// less than the per-person limit, we assume that MAX_SELECT_LENGTH items
// available is still a significant number) and there is no "go on the waiting
// list" option needed either.

const MAX_SELECT_LENGTH = 9;

export function HowManyPurchase({
  item,
  numberAlreadyPurchased,
  currentDetails,
  adjustDetails,
  amount,
  formattedPriceString,
  available,
  waitingList,
  myLimit,
  effectiveLimit,
}) {
  const { item_code, purchase_limit } = item;
  const onWaitingList = waitingList.position !== null;
  const require = amount === null ? "require" : "wish to purchase";

  const showSelectList = effectiveLimit <= MAX_SELECT_LENGTH;
  const offerWaitingList = showSelectList && effectiveLimit < myLimit;

  return (
    <Field>
      <Label>
        How many {numberAlreadyPurchased ? "additional " : ""} {item_code} do
        you {require}?
      </Label>
      {numberAlreadyPurchased ? (
        <Content italic>
          Number already purchased: {numberAlreadyPurchased}.
          {purchase_limit === null ? null : " Limit is " + purchase_limit + "."}
        </Content>
      ) : null}
      {offerWaitingList ? (
        <Message color="warning">
          <Message.Body>
            {ucFirst(item_code)} are almost sold out; there{" "}
            {effectiveLimit === 1 ? "is" : "are"} only {effectiveLimit}{" "}
            available.
          </Message.Body>
        </Message>
      ) : null}

      <Field kind="group">
        {showSelectList ? (
          <Fragment>
            <Control>
              <Select
                value={
                  currentDetails.quantity +
                  (currentDetails.waitlist_desired ? "-waitlist" : "")
                }
                onChange={(e) => {
                  const valueString = e.target.value;
                  const quantity = Number(valueString.replace(/-.*/, ""));
                  const waitlist_desired = valueString.match(/-waitlist/)
                    ? true
                    : null;
                  adjustDetails({
                    quantity,
                    waitlist_desired,
                  });
                }}
              >
                {[...Array(effectiveLimit + 1).keys()].map((i) => (
                  <option key={i} value={i}>
                    {i}
                  </option>
                ))}
                {offerWaitingList ? (
                  <option key="wl" value={`${effectiveLimit}-waitlist`}>
                    {effectiveLimit}, and
                    {onWaitingList ? " keep me on " : " add me to "}
                    the waiting list for more
                  </option>
                ) : null}
              </Select>
            </Control>
            <Element>
              {formattedPriceString ? `at ${formattedPriceString}` : null}
            </Element>
          </Fragment>
        ) : (
          <Fragment>
            <Control>
              <TrimmedInput
                value={currentDetails.quantity}
                inputMode="numeric"
                setValue={(v) =>
                  adjustDetails({ quantity: Number(v), waitlist_desired: null })
                }
              />
            </Control>
            <Element>
              {formattedPriceString ? `at ${formattedPriceString}` : null}
            </Element>
          </Fragment>
        )}
      </Field>
    </Field>
  );
}

export function RegistrationPurchaseItem({
  itemKey,
  item,
  participation,
  currentDetails,
  openFor,
  previouslyOpenFor,
  setDetails,
  attending,
  wasAttending,
}) {
  const {
    activity_id,
    item_code,
    purchase_limit,
    fee_categories,
    unavailable,
    questions,
    // FIXME: add ask_for / unused_selection_data mechanisms
  } = item;
  const previousPurchaseState = participation?.current_registration_item_data?.[
    activity_id
  ]?.purchase?.[item_code]?.current_state ?? { total_number_purchased: 0 };

  // FIXME: Show other inapplicable fee categories too, for information about
  // what the fees are for different types of people, so the registrant can
  // make sure they are getting the right one.
  const applicableCategories = fee_categories?.filter((c) => c.applicable);
  const feeInfo =
    applicableCategories && applicableCategories.length > 0
      ? applicableCategories[0]
      : {};
  const amount = feeInfo.amount ?? null;
  const cancellationPolicy = feeInfo.cancellation_policy;
  const formattedPriceString =
    amount === null
      ? null
      : Dollars.format(amount) +
        (purchase_limit === 1 ? "" : " each") +
        (feeInfo.description !== "" ? " (" + feeInfo.description + ")" : "");

  const { waitingList, available } = capacityInformation(item);

  const origWaitlistSetting = waitingList.position !== null ? true : null;
  const numberAlreadyPurchased = previousPurchaseState.total_number_purchased;
  const amountPreviouslyPaid = previousPurchaseState.total_amount_paid;
  const creditBalance = previousPurchaseState.credit_balance ?? 0;

  const myLimit =
    purchase_limit === null
      ? Infinity
      : purchase_limit - numberAlreadyPurchased;

  const effectiveLimit = Math.min(myLimit, available.toMe);
  const onePerPerson = item.purchase_limit === 1;

  const whichActivitySuffix =
    activity_id === participation.activity.id
      ? ""
      : " at the " + participation.activity.short_title;

  const adjustDetails = (changes, clearOtherChanges) => {
    // For any explicit quantity specification WITHOUT an explicit
    // waitlist_desired setting, set waitlist_desired to null. This is so
    // we don't have to repeat it in every call to adjustDetails where
    // a quantity is to be set. Similarly for total_amount_override (except
    // have it default to zero, not null, in the case of negative quantity)
    const defaults =
      changes.quantity === undefined
        ? {}
        : {
            waitlist_desired: null,
            total_amount_override: changes.quantity < 0 ? 0 : null,
          };

    const directItemChanges = { ...defaults, ...changes };

    const questionChanges = directItemChanges.question_responses;
    delete directItemChanges.question_responses;

    const defaultToGoOffWaitlist =
      openFor !== FORM_STATE.CLOSED &&
      attending &&
      origWaitlistSetting &&
      available.toMe > 0;

    const newDetails = {
      ...{
        unit_cost: amount ?? 0,
        quantity: defaultToGoOffWaitlist ? 1 : 0,
        waitlist_desired: defaultToGoOffWaitlist ? null : origWaitlistSetting,
        fee_selection_data: {},
        question_responses: {},
      },
      ...(clearOtherChanges ? {} : currentDetails ?? {}),
      ...directItemChanges,
    };

    newDetails.fee_selection_data = {
      ...(newDetails.fee_selection_data || {}),
    };
    newDetails.question_responses = {
      ...(newDetails.question_responses || {}),
    };

    adjustFeeQualifyingDetails(newDetails, item);

    if (newDetails.quantity > 0) {
      adjustQuestionResponses(newDetails.question_responses, questionChanges);
      newDetails.changed = true;
    } else if (newDetails.quantity < 0) {
      newDetails.question_responses = {};
      newDetails.waitlist_desired = null;
      newDetails.changed = true;
    } else {
      newDetails.question_responses = {};
      newDetails.changed = newDetails.waitlist_desired !== origWaitlistSetting;
    }
    newDetails.incomplete = null;
    if (newDetails.quantity === "") {
      if (onePerPerson)
        // Shouldn't happen with the yes/no component; reset to quantity 0
        newDetails.quantity = 0;
      else
        newDetails.incomplete =
          `Please specify how many ${item_code} you desire` +
          whichActivitySuffix;
    } else if (newDetails.quantity > effectiveLimit) {
      if (onePerPerson)
        // Shouldn't happen with the yes/no component; reset to quantity 0
        newDetails.quantity = 0;
      else
        newDetails.incomplete =
          effectiveLimit === myLimit
            ? `Please specify a maximum of ${effectiveLimit} ${item_code}` +
              whichActivitySuffix
            : `There are not that many ${item_code} available` +
              `${whichActivitySuffix}. Please specify a maximum of ${effectiveLimit}`;
    }
    setDetails(newDetails);
  };

  const [haveClearedDetails, setHaveClearedDetails] = useState(false);

  const participationState = useContext(participationStateContext);
  const resetForm =
    !currentDetails ||
    openFor !== previouslyOpenFor ||
    participationState.changed.baseData ||
    participationState.changed.itemBaseData[itemKey];

  useEffect(() => {
    if (resetForm && !haveClearedDetails) adjustDetails({}, true);
  });

  useEffect(() => {
    if (resetForm !== !haveClearedDetails) setHaveClearedDetails(resetForm);
  }, [resetForm, haveClearedDetails, setHaveClearedDetails]);

  // If the fee categories have changed, but we aren't doing a full form
  // reset, at least recalculate the total fee owing.
  const [haveRecalculated, setHaveRecalculated] = useState(false);
  const recalculate =
    participationState.changed.itemFeeData[itemKey] && !resetForm;

  useEffect(() => {
    if (recalculate && !haveRecalculated) adjustDetails({}, false);
  });

  useEffect(() => {
    if (recalculate !== haveRecalculated) setHaveRecalculated(recalculate);
  }, [recalculate, haveRecalculated, setHaveRecalculated]);

  const PurchaseErrorMessage = ({ children }) => {
    useEffect(() => {
      if (!currentDetails?.hasError) adjustDetails({ hasError: true });
    }, []);
    return <ErrorMessage>{children}</ErrorMessage>;
  };

  const [addMore, setAddMore] = useState(numberAlreadyPurchased === 0);

  if (fee_categories && applicableCategories.length === 0)
    return (
      <PurchaseErrorMessage>
        Internal error: Unsupported registration fee structure. Please contact
        Institute staff for assistance. Our apologies!
      </PurchaseErrorMessage>
    );
  if (unavailable) return null; // FIXME: deal with this in a better way?
  if (!currentDetails) return <div>Loading; please wait ...</div>;
  if (openFor === FORM_STATE.CANCEL)
    return amountPreviouslyPaid > 0 && wasAttending ? (
      <Message color="warning">
        <Message.Body>
          Please note that cancelling your registration does not automatically
          trigger a refund of the {Dollars.format(amountPreviouslyPaid)}{" "}
          previously paid for {onePerPerson ? a_or_an(item_code) : item_code}
          {whichActivitySuffix}.{" "}
          <RefundContactNotice ofWhat="part of this amount" isNew />
        </Message.Body>
      </Message>
    ) : null;

  if (openFor === FORM_STATE.CLOSED) return null;

  if (!attending) return null;

  const maxPurchased =
    purchase_limit !== null && numberAlreadyPurchased >= purchase_limit;

  const hidePurchaseComponents = maxPurchased
    ? true
    : numberAlreadyPurchased === 0
    ? false
    : addMore
    ? false
    : currentDetails.quantity > 0
    ? false
    : true;

  return (
    <Fragment>
      <Heading className="registration-item" size={5}>
        {ucFirstEachWord(item_code)}
      </Heading>
      {maxPurchased ? (
        <AlreadyPurchasedMaximumAllowable
          {...{
            item,
            numberAlreadyPurchased,
            amountPreviouslyPaid,
            currentDetails,
            adjustDetails,
          }}
        />
      ) : numberAlreadyPurchased > 0 ? (
        <AddMoreOrCancel
          {...{
            item,
            numberAlreadyPurchased,
            amountPreviouslyPaid,
            currentDetails,
            adjustDetails,
            addMore,
            setAddMore,
          }}
        />
      ) : null}
      {available.toMe > 0 && !hidePurchaseComponents ? (
        <AskFeeQualifyingData item={item} />
      ) : null}

      {hidePurchaseComponents ? null : onePerPerson || 0 === available.toMe ? (
        <YesNoPurchase
          {...{
            item,
            numberAlreadyPurchased,
            creditBalance,
            currentDetails,
            adjustDetails,
            amount,
            formattedPriceString,
            available,
            waitingList,
          }}
        />
      ) : (
        <HowManyPurchase
          {...{
            item,
            numberAlreadyPurchased,
            currentDetails,
            adjustDetails,
            amount,
            formattedPriceString,
            available,
            waitingList,
            myLimit,
            effectiveLimit,
          }}
        />
      )}

      {!maxPurchased &&
      amount > 0 &&
      available.toMe > 0 &&
      cancellationPolicy ? (
        <Field>
          <StructuredText data={cancellationPolicy} />
        </Field>
      ) : null}

      {currentDetails.quantity > 0 ? (
        <PromptQuestions
          questions={questions}
          currentAnswers={currentDetails.question_responses}
          adjustAnswers={(newAnswers) =>
            adjustDetails({ question_responses: newAnswers })
          }
        />
      ) : null}
    </Fragment>
  );
}
